import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import useClickOutside from '../../hooks/useClickOutside'
import useAuthStore from '../../stores/authStore'
import useEditStore from '../../controllers/EditController'
import Logo from '../common/Logo'

function StatusBar({ onExport, hasImage }) {
  const [isFileMenuOpen, setIsFileMenuOpen] = useState(false)
  const [isEditMenuOpen, setIsEditMenuOpen] = useState(false)
  const navigate = useNavigate()
  const fileMenuRef = useRef(null)
  const editMenuRef = useRef(null)
  const logout = useAuthStore(state => state.logout)
  const { undo, redo, canUndo, canRedo } = useEditStore()

  useClickOutside(fileMenuRef, () => setIsFileMenuOpen(false))
  useClickOutside(editMenuRef, () => setIsEditMenuOpen(false))

  const handleLogout = async () => {
    await logout()
    navigate('/auth')
  }

  const handleExport = () => {
    if (!hasImage || !onExport) {
      console.warn('No image to export')
      return
    }

    try {
      const imageData = onExport()
      if (!imageData) {
        console.error('Failed to export image')
        return
      }

      // Create download link
      const link = document.createElement('a')
      link.href = imageData
      link.download = `photowhim-export-${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      setIsFileMenuOpen(false)
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  return (
    <div className="fixed top-0 left-0 right-0 h-8 bg-gray-900 border-b border-gray-800 flex items-center justify-between px-4 z-[100]">
      <div className="flex items-center gap-4">
        <Logo size="sm" className="mr-2" />
        <div className="flex gap-2">
          <div className="relative" ref={fileMenuRef}>
            <button
              onClick={() => {
                setIsFileMenuOpen(!isFileMenuOpen)
                setIsEditMenuOpen(false)
              }}
              className="text-gray-300 hover:text-white text-sm px-2 py-1 rounded hover:bg-gray-800"
            >
              File
            </button>
            
            {isFileMenuOpen && (
              <div className="absolute top-full left-0 mt-1 bg-gray-900 border border-gray-800 rounded-lg shadow-lg py-1 min-w-[160px] z-[60]">
                <button
                  onClick={handleExport}
                  disabled={!hasImage}
                  className={`w-full text-left px-4 py-2 text-sm ${
                    hasImage 
                      ? 'text-gray-300 hover:bg-gray-800' 
                      : 'text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Export
                </button>
              </div>
            )}
          </div>

          <div className="relative" ref={editMenuRef}>
            <button
              onClick={() => {
                setIsEditMenuOpen(!isEditMenuOpen)
                setIsFileMenuOpen(false)
              }}
              className="text-gray-300 hover:text-white text-sm px-2 py-1 rounded hover:bg-gray-800"
            >
              Edit
            </button>
            
            {isEditMenuOpen && (
              <div className="absolute top-full left-0 mt-1 bg-gray-900 border border-gray-800 rounded-lg shadow-lg py-1 min-w-[160px] z-[60]">
                <button
                  onClick={undo}
                  disabled={!canUndo}
                  className={`w-full text-left px-4 py-2 text-sm ${
                    canUndo ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Undo
                  <span className="ml-2 text-gray-500">⌘Z</span>
                </button>
                <button
                  onClick={redo}
                  disabled={!canRedo}
                  className={`w-full text-left px-4 py-2 text-sm ${
                    canRedo ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Redo
                  <span className="ml-2 text-gray-500">⇧⌘Z</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    
      <button
        onClick={handleLogout}
        className="text-gray-300 hover:text-white text-sm flex items-center gap-2 px-3 py-1 rounded hover:bg-gray-800"
      >
        Logout
      </button>
    </div>
  )
}

export default StatusBar